import {CREATE, UPDATE, GET_LIST, GET_ONE, GET_MANY} from 'react-admin';

/**
 * Decorator for drip-campaigns related requests
 * Handles IRI transformation for API Platform requirements and response transformation
 *
 * @param requestHandler
 * @returns {function(...[*]=)}
 */
export const dripCampaignsDecorator = requestHandler => async (type, resource, params) => {
    if (resource !== 'drip-campaigns' && resource !== 'drip-enrollments') {
        return requestHandler(type, resource, params);
    }

    // Transform request data for CREATE and UPDATE
    if (resource === 'drip-campaigns' && (type === CREATE || type === UPDATE)) {
        params = transformToIRIFormat(params);
    }

    const response = await requestHandler(type, resource, params);

    // Transform response data for GET operations
    if (resource === 'drip-campaigns' && (type === GET_LIST || type === GET_ONE || type === GET_MANY)) {
        response.data = transformResponseData(response.data);
    }

    return response;
};

/**
 * Transform UUID references to IRI format for API Platform
 *
 * @param params
 * @returns {*}
 */
const transformToIRIFormat = (params) => {
    const {data} = params;

    if (data.message && !data.message.startsWith('/')) {
        params.data.message = `/messages/${data.message}`;
    }

    if (data.targetGroup && !data.targetGroup.startsWith('/')) {
        params.data.targetGroup = `/groups/${data.targetGroup}`;
    }

    if (data.sender && !data.sender.startsWith('/')) {
        // Use /campaign/senders/ format to match the API
        params.data.sender = `/campaign/senders/${data.sender}`;
    }

    return params;
};

/**
 * Transform API response data to frontend format
 * Handles new enrollment statistics structure and report data
 *
 * @param data
 * @returns {*}
 */
const transformResponseData = (data) => {
    const transformSingleRecord = (record) => {
        // Handle enrollment statistics if present
        if (record.enrollments) {
            record.totalEnrollments = record.enrollments.total || 0;
            record.queuedEnrollments = record.enrollments.queued || 0;
            record.sentEnrollments = record.enrollments.sent || 0;
            record.failedEnrollments = record.enrollments.failed || 0;
            record.cancelledEnrollments = record.enrollments.cancelled || 0;
            record.skippedEnrollments = record.enrollments.skipped || 0;

            // Add rates
            record.sentRate = record.enrollments.sentRate || 0;
            record.failureRate = record.enrollments.failureRate || 0;
            record.queuedRate = record.enrollments.queuedRate || 0;

            // Add scheduling info
            record.lastEnrolledAt = record.enrollments.lastEnrolledAt;
            record.nextScheduledAt = record.enrollments.nextScheduledAt;
        }

        // Handle report data if present - pass through as is
        // The report object contains performance metrics that will be displayed in the Show view
        if (record.report) {
            // Report object is already in the correct format, just ensure it's preserved
            record.report = {
                ...record.report
            };
        }

        // Extract IDs from IRI format for Edit component
        // The Edit component expects the ID values, not the IRIs
        if (record.targetGroup) {
            if (record.targetGroup.startsWith('/groups/')) {
                record.targetGroupId = record.targetGroup.replace('/groups/', '');
                // Also set targetGroup to the ID for the Edit form
                record.targetGroup = record.targetGroupId;
            }
        }

        if (record.message) {
            if (record.message.startsWith('/messages/')) {
                record.messageId = record.message.replace('/messages/', '');
                // Also set message to the ID for the Edit form
                record.message = record.messageId;
            }
        }

        if (record.sender) {
            // Handle both /senders/ and /campaign/senders/ formats
            if (record.sender.startsWith('/senders/')) {
                record.senderId = record.sender.replace('/senders/', '');
                // Also set sender to the ID for the Edit form
                record.sender = record.senderId;
            } else if (record.sender.startsWith('/campaign/senders/')) {
                record.senderId = record.sender.replace('/campaign/senders/', '');
                // Also set sender to the ID for the Edit form
                record.sender = record.senderId;
            }
        }

        // Handle baseCampaign field if it exists (for backward compatibility)
        if (record.baseCampaign && record.baseCampaign.startsWith('/campaigns/')) {
            record.baseCampaignId = record.baseCampaign.replace('/campaigns/', '');
        }

        return record;
    };

    if (Array.isArray(data)) {
        return data.map(transformSingleRecord);
    } else {
        return transformSingleRecord(data);
    }
};